/**
 * Correlation Context Provider
 *
 * Provides correlation IDs for request tracking across the application.
 * Correlation IDs help trace user actions across multiple API calls and components.
 *
 * Usage:
 *   <CorrelationProvider>
 *     <App />
 *   </CorrelationProvider>
 *
 * In components:
 *   const { correlationId, setCorrelation } = useCorrelation();
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import logger from '../utils/logger';

const CorrelationContext = createContext(null);

/**
 * Generate a unique correlation ID
 * Format: timestamp-random (e.g., 1698765432-a1b2c3d4)
 */
const generateCorrelationId = () => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 10);
  return `${timestamp}-${random}`;
};

export function CorrelationProvider({ children }) {
  const [correlationId, setCorrelationId] = useState(generateCorrelationId());
  const [sessionId] = useState(generateCorrelationId()); // Session-level ID
  const [metadata, setMetadata] = useState({});

  // Update logger context whenever correlation data changes
  useEffect(() => {
    logger.setContext({
      correlationId,
      sessionId,
      ...metadata,
    });
  }, [correlationId, sessionId, metadata]);

  /**
   * Set custom correlation data
   * @param {Object} data - Additional correlation data (e.g., userId, action)
   */
  const setCorrelation = useCallback((data) => {
    setMetadata((prev) => ({ ...prev, ...data }));
  }, []);

  /**
   * Generate a new correlation ID (e.g., for new user action)
   */
  const refreshCorrelationId = useCallback(() => {
    const newId = generateCorrelationId();
    setCorrelationId(newId);
    logger.debug('Correlation ID refreshed', { newCorrelationId: newId });
  }, []);

  /**
   * Clear custom correlation metadata
   */
  const clearCorrelation = useCallback(() => {
    setMetadata({});
  }, []);

  const value = {
    correlationId,
    sessionId,
    metadata,
    setCorrelation,
    refreshCorrelationId,
    clearCorrelation,
  };

  return <CorrelationContext.Provider value={value}>{children}</CorrelationContext.Provider>;
}

CorrelationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook to use correlation context
 * @returns {Object} Correlation context value
 */
export function useCorrelation() {
  const context = useContext(CorrelationContext);
  if (!context) {
    throw new Error('useCorrelation must be used within CorrelationProvider');
  }
  return context;
}

/**
 * HOC to wrap component with correlation context
 */
export function withCorrelation(Component) {
  return function CorrelationWrappedComponent(props) {
    return (
      <CorrelationProvider>
        <Component {...props} />
      </CorrelationProvider>
    );
  };
}

export default CorrelationContext;
